/**
 * useGridLayers.ts
 * Hook for managing grid visualizations on the map
 */

import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DEFAULT_STEP } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { isOrdinalType } from "@/utils/dataset";
import { addLayer } from "@/utils/map";
import { addGridOrdinalLayers, addGridQuantileLayers } from "@/utils/map/grid";
import { addCentroidSource, addPolygonSource } from "@/utils/map/polygon";

type UseGridLayersProps = {
  map: Map;
  prepareLayerStyles: (
    parsedPolygonData: FeatureCollection<Geometry>,
    dataset: Dataset,
    step: number
  ) => { updatedStyle: DatasetStyle };
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;
};
export const useGridLayers = ({
  map,
  handleFeatureClick,
  handleMovePositionToLayer,
  prepareLayerStyles,
  handleUpdateSourceWithRank
}: UseGridLayersProps) => {
  const dispatch = useDispatch();
  const { isNewUIDatasetConfigEnabled, isRankingLabelEnabled } =
    useAppFeatures();

  const handleAddGridLayer = async (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => {
    if (!polygonData || !map || map.getSource(dataset.data.id)) return;

    const newLayer = {
      id: dataset.data.id,
      type: dataset.properties.spatialType,
      sublayers: {
        fill: `${dataset.data.id}-fill`,
        outline: `${dataset.data.id}-outline`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };
    const { updatedStyle } = prepareLayerStyles(
      polygonData,
      dataset,
      dataset.style.colorSteps || DEFAULT_STEP
    );

    addPolygonSource(map, newLayer.id, polygonData);
    addCentroidSource(map, `${newLayer.id}-centroid`, polygonData);

    const isOrdinal = isOrdinalType(dataset.selectedCategory.data_type);

    addLayer({
      isNewUIDatasetConfigEnabled,
      isRankingLabelEnabled,
      map,
      layerData: newLayer,
      style: updatedStyle,
      selectedCategory: dataset.selectedCategory.attribute_name,
      isOrdinal,
      addOrdinalFunc: addGridOrdinalLayers,
      addQuantileFunc: addGridQuantileLayers
    });

    handleUpdateSourceWithRank({
      datasetId: `${newLayer.id}-centroid`,
      style: updatedStyle,
      update: {}
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );
    handleFeatureClick(newLayer.sublayers.fill);
    handleMovePositionToLayer(polygonData);
  };

  return {
    handleAddGridLayer
  };
};

export default useGridLayers;
